import type { ChangeEvent, KeyboardEvent } from 'react';

import {
  FormControl,
  Box,
  FormHelperText,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';

interface PromptFieldProps {
  value: string;
  error: boolean | string;
  setValue: (value: string) => void;
  setError: (value: boolean | string) => void;
  onSubmit: () => void;
  tooltip?: string;
  generating: boolean;
  stopGeneration: () => void;
  disabled?: boolean;
  [key: string]: unknown;
}

export default function PromptField({
  value,
  error,
  setValue,
  setError,
  onSubmit,
  tooltip,
  generating,
  stopGeneration,
  ...rest
}: PromptFieldProps) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: newValue } = event.target;
    setValue(newValue);
    if (newValue === '') {
      setError('Prompt cannot be blank');
    } else {
      setError(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.which === 13) {
      //<------use enter key to submit form
      event.preventDefault();
      if (!generating && !rest.disabled) {
        onSubmit();
      }
    }
  };

  const textField = (
    <Box>
      <FormControl fullWidth>
        <TextField
          {...rest}
          label='Prompt'
          id='prompt'
          aria-describedby='prompt-helper-text'
          onChange={handleChange}
          error={Boolean(error)}
          onKeyDown={handleKeyDown}
          value={value}
          variant='outlined'
          multiline
          disabled={rest.disabled || generating}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {generating ? (
                  <Fab
                    size='small'
                    color='primary'
                    aria-label='stop generation'
                    onClick={stopGeneration}
                    sx={{
                      minWidth: '40px',
                      minHeight: '40px',
                      boxShadow: 2,
                      zIndex: 1,
                    }}
                  >
                    <StopIcon />
                  </Fab>
                ) : (
                  <IconButton
                    aria-label='send prompt'
                    onClick={onSubmit}
                    onMouseDown={onSubmit}
                    edge='end'
                    disabled={rest.disabled}
                  >
                    <SendIcon color='primary' />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
        />
        {error ? (
          <FormHelperText id='prompt-helper-text'>{error}</FormHelperText>
        ) : rest.disabled ? (
          <FormHelperText id='prompt-helper-text' color='text.secondary'>
            Please select a Document Type Filter before asking a question.
          </FormHelperText>
        ) : (
          <FormHelperText id='prompt-helper-text'>
            Enter a question to ask your documents.
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement='top'>
      {textField}
    </Tooltip>
  ) : (
    textField
  );
}

